import HttpService from './HttpService';
import analytics from './AnalyticsService';
import type { Item, ItemPage, Room, ItemImage } from '../types/item';

/** A file paired with the scan slot it was uploaded into. */
export interface ScanSlotFile {
  slot: 'photo' | 'receipt' | 'manual' | 'tag';
  file: File;
}

/** A stored file the item re-scan left out, and why. */
export interface SkippedScanFile {
  id: number;
  original_filename: string;
  image_type: ScanSlotFile['slot'];
  reason: 'too_large' | 'unavailable';
}

/** Query params of GET /items. The server clamps `limit` to ITEMS_MAX_PAGE_SIZE. */
export interface ItemPageParams {
  limit?: number;
  offset?: number;
}

/** Largest page GET /items will return — the server clamps anything above it. */
export const ITEMS_MAX_PAGE_SIZE = 200;

/** Response of POST /items/:id/smart-scan. */
export interface ItemSmartScanResponse {
  success: boolean;
  items: Record<string, unknown>[];
  skipped_files: SkippedScanFile[];
}

class InventoryService {
  private api: typeof HttpService;

  constructor() {
    this.api = HttpService;
  }

  /**
   * One page of the user's items (GET /items, offset-paginated: default 50,
   * max 200). Prefer getAllItems() when a screen needs the whole inventory.
   */
  async getItems(params: ItemPageParams = {}): Promise<ItemPage> {
    const response = await this.api.get('/items', { params });
    // Guard against 204 / null response bodies the same way getRooms does.
    const data = response.data;
    return {
      items: Array.isArray(data?.items) ? data.items : [],
      pagination: data?.pagination ?? {
        total: 0,
        limit: params.limit ?? 50,
        offset: params.offset ?? 0,
        has_more: false,
      },
    };
  }

  /**
   * The whole inventory, assembled by walking every page of GET /items.
   *
   * For the screens that genuinely need all of it: the inventory list
   * (search, category filter and sort run client-side), the dashboard
   * valuation, and the task form's item picker. Pages at the server maximum
   * so a 1000-item inventory (the paid cap) is five round trips. Stops on
   * `has_more === false` OR an empty page, so a malformed envelope can never
   * spin forever.
   */
  async getAllItems(): Promise<Item[]> {
    const all: Item[] = [];
    let offset = 0;
    for (;;) {
      const { items, pagination } = await this.getItems({ limit: ITEMS_MAX_PAGE_SIZE, offset });
      all.push(...items);
      if (!pagination.has_more || items.length === 0) break;
      offset += items.length;
    }
    return all;
  }

  async getItem(id: number | string): Promise<Item> {
    const response = await this.api.get(`/items/${id}`);
    return response.data;
  }

  async createItem(itemData: Partial<Item>): Promise<Item> {
    const response = await this.api.post('/items', itemData);
    return response.data;
  }

  async updateItem(id: number | string, itemData: Partial<Item>): Promise<Item> {
    const response = await this.api.put(`/items/${id}`, itemData);
    return response.data;
  }

  async deleteItem(id: number | string): Promise<unknown> {
    const response = await this.api.delete(`/items/${id}`);
    return response.data;
  }

  async getRooms(): Promise<Room[]> {
    const response = await this.api.get('/rooms', { params: { limit: 100 } });
    // Guard against 204 / null response bodies — accessing .rooms on undefined throws.
    return response.data?.rooms || [];
  }

  async smartScan(file: File): Promise<unknown> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await this.api.post('/smart-scan', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }

  // Multi-file slot-aware scan. Sends all uploaded files (any combination of
  // photo / receipt / manual / tag) to Gemini in a single multimodal call and
  // returns one merged item record. Counts as one scan against the user's
  // monthly quota, regardless of file count.
  //
  // slotFiles: Array<{ slot: 'photo'|'receipt'|'manual'|'tag', file: File }>
  async smartScanMultiple(slotFiles: ScanSlotFile[]): Promise<unknown> {
    const formData = new FormData();
    for (const { slot, file } of slotFiles) {
      formData.append('images', file);
      formData.append('image_types', slot);
    }
    // Forward the active analytics session so the backend's
    // inventory.scan_completed_server event can be joined to the
    // frontend's inventory.scan_initiated / inventory.scan_completed
    // events in the funnel. Without this, the server event always
    // sees scan_session_id=None and the join breaks.
    const sid = analytics.getSessionId();
    if (sid) {
      formData.append('scan_session_id', sid);
    }
    const response = await this.api.post('/smart-scan', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  }

  // Re-run Smart Autofill over the photos and documents already stored on an
  // existing item (the edit page), so files added after creation can still
  // fill in details. The server reads the files itself; nothing is uploaded
  // and nothing is saved — the caller reviews the suggestions. One scan.
  async smartScanItem(itemId: number | string): Promise<ItemSmartScanResponse> {
    const sid = analytics.getSessionId();
    const response = await this.api.post(
      `/items/${itemId}/smart-scan`,
      sid ? { scan_session_id: sid } : {}
    );
    return response.data;
  }


  async uploadItemImage(
    itemId: number | string,
    file: File,
    isPrimary = false,
    imageType = 'photo'
  ): Promise<ItemImage> {
      const formData = new FormData();
      formData.append('image', file);
      if (isPrimary) formData.append('is_primary', 'true');
      formData.append('image_type', imageType);

      const response = await this.api.post(`/items/${itemId}/images`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
  }

  async deleteItemImage(itemId: number | string, imageId: number | string): Promise<unknown> {
    const response = await this.api.delete(`/items/${itemId}/images/${imageId}`);
    return response.data;
  }

  async setItemImagePrimary(itemId: number | string, imageId: number | string): Promise<unknown> {
    // Body-less PUT — the endpoint derives everything from the URL.
    const response = await this.api.put(`/items/${itemId}/images/${imageId}/primary`);
    return response.data;
  }

  async getCategories(): Promise<unknown> {
    const response = await this.api.get('/items/categories');
    return response.data;
  }
}

export default new InventoryService();
